import React, { useRef, useState, ReactElement } from 'react';
import classnames from 'classnames';

import Icon from 'components/Icon';
import { colors } from 'constants/colors';
import { ColorNames } from 'constants/colorNames';
import { ICON_COLORS } from 'components/Button/new/icon-colors';
import { useOutsideElementClick } from 'hooks/useOutsideElementClick';

import styles from './OptionsContainer.module.scss';

const DEFAULT_OPTION_CONTAINER_STYLE = 'contextual-light';

const ARROW_BY_DIRECTION = {
  left: 'arrowLeft',
  right: 'arrowRight',
} as const;

const normalizeSubItems = (option: IOption) =>
  option.subItems?.length ? option.subItems : undefined;

export interface IOption {
  type: string;
  label: string;
  onClick?: () => void;
  subItems?: IOption[];
  submenuDirection?: 'left' | 'right';
  style?: string;
  shouldShowIcon?: boolean;
  iconColor?: string;
  optionColorName?: ColorNames;
}

export type OptionContainerStyle =
  | 'main'
  | 'contextual'
  | 'contextual-light'
  | 'contextual-positive'
  | 'contextual-transparent';

interface Props {
  customButtonStyle?: string;
  isDark?: boolean;
  isLeftAlign?: boolean;
  isLightButton?: boolean;
  isMedium?: boolean;
  isSmall?: boolean;
  optionButtonIcon?: string;
  optionButtonInitialColor?: string;
  optionButtonLabel?: string;
  optionContainerStyle?: OptionContainerStyle;
  options?: IOption[];
  optionsWrapperClassName?: string;
  optionsIconClassName?: string;
  testId?: string;
  wrapperClassName?: string;
  customOptions?: ReactElement[];
  disabled?: boolean;
  optionClassName?: string;
}

const OptionsContainer: React.FC<Props> = ({
  customButtonStyle,
  isDark,
  isLeftAlign,
  isLightButton,
  isMedium,
  isSmall,
  optionButtonIcon,
  optionButtonInitialColor,
  optionButtonLabel,
  optionContainerStyle,
  optionsIconClassName,
  options,
  optionsWrapperClassName,
  testId,
  wrapperClassName,
  customOptions,
  disabled,
  optionClassName,
}) => {
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const styleKey = optionContainerStyle ?? DEFAULT_OPTION_CONTAINER_STYLE;
  const containerStyle = styles[styleKey];

  useOutsideElementClick([optionsContainerRef], () => setShowOptions(false));

  const getIconColor = (customIdleColor?: string) => {
    const { idle, active } = ICON_COLORS[styleKey];
    if (showOptions && styleKey !== 'contextual-transparent') return active;
    return customIdleColor ?? idle;
  };

  const renderOptionButton = (option: IOption, isSubItem?: boolean) => {
    const subItems = normalizeSubItems(option);
    const hasSubItems = !!subItems;
    const shouldCloseOnClick = !hasSubItems;
    const submenuDirection = option.submenuDirection ?? 'right';
    const arrowIconName = hasSubItems
      ? ARROW_BY_DIRECTION[submenuDirection]
      : null;
    const handleOptionClick = () => {
      if (disabled) return;
      if (shouldCloseOnClick) {
        setShowOptions(false);
      }
      option.onClick?.();
    };

    const handleOptionMouseEnter = () => {
      if (hasSubItems) return setActiveSubmenu(option.label);
      if (!isSubItem) setActiveSubmenu(null);
    };

    return (
      <button
        key={option.label}
        className={classnames(
          styles.option,
          containerStyle,
          option.style,
          optionClassName,
          [
            styles[
              `option-${option.optionColorName || ColorNames.primaryColor400}`
            ],
          ],
          {
            [styles['option-subitem']]: isSubItem,
          },
        )}
        onClick={handleOptionClick}
        onMouseEnter={handleOptionMouseEnter}
        type="button"
      >
        <span className={styles['option-left-slot']}>
          {hasSubItems && option?.shouldShowIcon && (
            <Icon
              name={option.type}
              size={1}
              color={option.iconColor || colors.primaryColor400}
            />
          )}
          {hasSubItems && arrowIconName && (
            <Icon
              name={arrowIconName}
              size={0.75}
              color={colors.uiColorBlack}
            />
          )}
        </span>

        <span className={styles['option-label']}>{option.label}</span>

        <span className={styles['option-icons']}>
          {!hasSubItems && option?.shouldShowIcon && (
            <Icon
              name={option.type}
              size={1}
              color={option.iconColor || colors.primaryColor400}
            />
          )}
        </span>
      </button>
    );
  };

  const renderOptions = () =>
    customOptions ||
    options?.map(option => {
      const subItems = normalizeSubItems(option);
      const hasSubItems = !!subItems;
      if (!hasSubItems) return renderOptionButton(option);

      return (
        <div
          key={option.label}
          className={styles['option-with-submenu']}
          onMouseLeave={() => setActiveSubmenu(null)}
        >
          {renderOptionButton(option)}
          <div
            className={classnames(
              styles.submenu,
              styles[`submenu-${option.submenuDirection ?? 'right'}`],

              {
                [styles['submenu-show']]: activeSubmenu === option.label,
              },
            )}
          >
            {subItems?.map(subItem => renderOptionButton(subItem, true))}
          </div>
        </div>
      );
    }) ||
    null;

  return (
    <div className={classnames(styles['options-container'], wrapperClassName)}>
      <div
        data-testid={testId}
        className={classnames(
          styles['options-button'],
          customButtonStyle,
          containerStyle,
          {
            [styles['active']]: showOptions,
            [styles['small']]: isSmall,
            [styles['medium']]: isMedium,
            [styles['dark']]: isDark,
            [styles['light-button']]: isLightButton,
            [styles['disabled']]: disabled,
          },
        )}
        role="button"
        onClick={() => !disabled && setShowOptions(true)}
      >
        {optionButtonIcon && (
          <div
            className={classnames(
              styles['options-button-icon-container'],
              optionsIconClassName,
            )}
          >
            <Icon
              name={optionButtonIcon}
              color={getIconColor(optionButtonInitialColor)}
            />
          </div>
        )}
        {optionButtonLabel}
      </div>
      <div
        ref={optionsContainerRef}
        className={classnames(
          styles.options,
          containerStyle,
          {
            [styles.show]: showOptions,
            [styles.left]: isLeftAlign,
          },
          optionsWrapperClassName,
        )}
      >
        {renderOptions()}
      </div>
    </div>
  );
};

export default OptionsContainer;
